import React, { useState, useEffect } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { styles } from "./stylesFaq";
import ArrowFaq from "@/assets/svg/ArrowFaq";
import { useGetFAQQuery } from "@/store/api/faqApi";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";

const toSlug = (title: string) =>
  title.toLowerCase().replace(/[?:]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").trim();

const Faq: React.FC = () => {
  const { data: faq, isLoading } = useGetFAQQuery();
  const location = useLocation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | false>(false);

  useEffect(() => {
    if (!faq) return;
    const hash = location.hash.replace("#", "");
    if (!hash) {
      setExpanded(false);
      return;
    }
    const match = faq.find((item) => toSlug(item.title) === hash);
    if (match) setExpanded(toSlug(match.title));
  }, [location.hash, faq]);

  const handleChange = (slug: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded) {
      navigate(`#${slug}`, { replace: true });
    } else {
      navigate(location.pathname, { replace: true });
      setExpanded(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 100px)",
        }}
      >
        <CustomLoader />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ padding: "16px" }}>
        <Typography variant="h2" sx={styles.sectionTitle}>
          Frequently Asked Questions (FAQ)
        </Typography>
        <Box display="flex" flexDirection="column" gap="10px">
          {(faq ?? []).map((item) => {
            const slug = toSlug(item.title);
            return (
              <Accordion
                key={item.id}
                expanded={expanded === slug}
                onChange={handleChange(slug)}
                sx={{
                  maxWidth: "964px",
                  padding: "0 24px",
                  border: "1px solid #E4E7E9",
                  borderRadius: "12px !important",
                  boxShadow: "none",
                  backgroundColor: "transparent",
                  "&:before": { display: "none" },
                  "&.Mui-expanded": { border: "1px solid #2A85FF", margin: 0, paddingBottom: "18px" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ArrowFaq />}
                  sx={{
                    margin: "0px",
                    padding: "0px",
                    minHeight: "60px",
                    height: "60px",
                    "&.Mui-expanded": { minHeight: "60px" },
                    "& .MuiAccordionSummary-content": { margin: 0 },
                    "& .MuiAccordionSummary-expandIconWrapper": {
                      color: "#7D8690",
                      transition: "all 0.3s",
                    },
                    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                      color: "#2A85FF",
                      transform: "rotate(180deg)",
                    },
                  }}
                >
                  <Typography
                    sx={{ fontWeight: 500, fontSize: "16px", lineHeight: "22px", color: "#2A2D31" }}
                  >
                    {item.title}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ padding: "0px" }}>
                  <Box
                    sx={{
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "22px",
                      color: "#2A2D31",
                      "& *": { margin: 0 },
                      "& ul, & ol": { paddingLeft: "20px" },
                    }}
                    dangerouslySetInnerHTML={{ __html: item.content }}
                  />
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default Faq;
